import { secureStorage } from './secureStorage';

interface ApiResponse<T = any> {
  data?: T;
  items?: T;
  detail?: string;
}

interface RequestOptions {
  signal?: AbortSignal;
}

class ApiClient {
  private baseUrl: string;
  private token: string | null = null;
  private teamId: string | null = null;

  constructor() {
    this.baseUrl = import.meta.env.VITE_BACKEND_URL || 'http://127.0.0.1:8000';
  }

  setAuth(token: string, teamId?: string) {
    this.token = token;
    this.teamId = teamId || null;
  }

  updateTeamId(teamId: string) {
    this.teamId = teamId;
  }

  clearAuth() {
    this.token = null;
    this.teamId = null;
  }

  getAuthHeaders(): Record<string, string> {
    const headers: Record<string, string> = {};

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    if (this.teamId) {
      headers['team_id'] = this.teamId;
    }

    return headers;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    contentType: string = 'application/json',
  ): Promise<T> {
    const url = `${this.baseUrl}/api${endpoint}`;

    const headers: Record<string, string> = {
      'Content-Type': contentType,
    };

    if (options.headers) {
      Object.entries(options.headers as Record<string, string>).forEach(
        ([key, value]) => {
          headers[key] = value;
        },
      );
    }

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    if (this.teamId) {
      headers['team_id'] = this.teamId;
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });

      if (!response.ok) {
        if (response.status === 401 && !url.includes('/connectors/')) {
          this.clearAuth();
          secureStorage.clear();
        }

        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;

        try {
          const errorData = await response.json();
          if (errorData.detail) {
            errorMessage = errorData.detail;
          }
        } catch {
          // Response body is not JSON, use status text
        }

        throw new Error(errorMessage);
      }

      // Handle 204 No Content response
      if (response.status === 204) {
        return {} as T;
      }

      const data: ApiResponse<T> = await response.json();

      if (data && data.detail) {
        throw new Error(data.detail);
      }

      return data as T;
    } catch (error) {
      throw error;
    }
  }

  async get<T>(
    endpoint: string,
    params?: Record<string, string | number>,
    options?: RequestOptions,
  ): Promise<T> {
    let url = endpoint;
    if (params) {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        searchParams.append(key, value.toString());
      });
      url += `?${searchParams.toString()}`;
    }

    return this.request<T>(url, {
      method: 'GET',
      signal: options?.signal,
    });
  }

  async post<T>(
    endpoint: string,
    body?: any,
    contentType?: string,
    options?: RequestOptions,
  ): Promise<T> {
    const isFormEncoded = contentType === 'application/x-www-form-urlencoded';
    const processedBody = body
      ? isFormEncoded
        ? new URLSearchParams(body).toString()
        : JSON.stringify(body)
      : undefined;

    return this.request<T>(
      endpoint,
      {
        method: 'POST',
        body: processedBody,
        signal: options?.signal,
      },
      contentType,
    );
  }

  async put<T>(
    endpoint: string,
    body?: any,
    contentType?: string,
    options?: RequestOptions,
  ): Promise<T> {
    const isFormEncoded = contentType === 'application/x-www-form-urlencoded';
    const processedBody = body
      ? isFormEncoded
        ? new URLSearchParams(body).toString()
        : JSON.stringify(body)
      : undefined;

    return this.request<T>(
      endpoint,
      {
        method: 'PUT',
        body: processedBody,
        signal: options?.signal,
      },
      contentType,
    );
  }

  async patch<T>(
    endpoint: string,
    body?: any,
    contentType?: string,
    options?: RequestOptions,
  ): Promise<T> {
    const isFormEncoded = contentType === 'application/x-www-form-urlencoded';
    const processedBody = body
      ? isFormEncoded
        ? new URLSearchParams(body).toString()
        : JSON.stringify(body)
      : undefined;

    return this.request<T>(
      endpoint,
      {
        method: 'PATCH',
        body: processedBody,
        signal: options?.signal,
      },
      contentType,
    );
  }

  async delete<T>(
    endpoint: string,
    body?: any,
    options?: RequestOptions,
  ): Promise<T> {
    const processedBody = body ? JSON.stringify(body) : undefined;

    return this.request<T>(endpoint, {
      method: 'DELETE',
      body: processedBody,
      signal: options?.signal,
    });
  }

  async postForm<T>(endpoint: string, formData: FormData): Promise<T> {
    const headers: Record<string, string> = {};

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    if (this.teamId) {
      headers['team_id'] = this.teamId;
    }

    const url = `${this.baseUrl}/api${endpoint}`;

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: formData,
      });

      if (!response.ok) {
        if (response.status === 401 && !url.includes('/connectors/')) {
          this.clearAuth();
          secureStorage.clear();
        }

        if (response.status === 406) {
          try {
            const errorData = await response.json();
            return errorData as T;
          } catch {
            // If parsing fails, fall back to generic error
          }
        }

        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;

        try {
          const errorData = await response.json();
          if (errorData.detail) {
            errorMessage = errorData.detail;
          }
        } catch {
          // Response body is not JSON, use status text
        }

        throw new Error(errorMessage);
      }

      // Handle 204 No Content response
      if (response.status === 204) {
        return {} as T;
      }

      const data: ApiResponse<T> = await response.json();

      if (data && data.detail) {
        throw new Error(data.detail);
      }

      return (data.data || data.items || data) as T;
    } catch (error) {
      throw error;
    }
  }

  async postStream(
    endpoint: string,
    body: any,
    onChunk: (chunk: string) => void,
    onComplete?: () => void,
    onError?: (error: Error) => void,
  ): Promise<void> {
    const url = `${this.baseUrl}/api${endpoint}`;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    if (this.teamId) {
      headers['team_id'] = this.teamId;
    }

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        if (response.status === 401 && !url.includes('/connectors/')) {
          this.clearAuth();
          secureStorage.clear();
        }

        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;

        try {
          const errorData = await response.json();
          if (errorData.detail) {
            errorMessage = errorData.detail;
          }
        } catch {
          // Response body is not JSON, use status text
        }

        const error = new Error(errorMessage);
        if (onError) {
          onError(error);
        } else {
          throw error;
        }
        return;
      }

      if (!response.body) {
        throw new Error('Response body is null');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      try {
        while (true) {
          const { done, value } = await reader.read();

          if (done) {
            if (onComplete) {
              onComplete();
            }
            break;
          }

          const chunk = decoder.decode(value, { stream: true });
          onChunk(chunk);
        }
      } finally {
        reader.releaseLock();
      }
    } catch (error) {
      if (onError) {
        onError(error as Error);
      } else {
        throw error;
      }
    }
  }
}

export const apiClient = new ApiClient();
